import { type QueryState } from "../hooks/useQueryWithState";
import { formatDollars } from "../lib/translate";
import { signalLifecycleState, type SignalLifecycleState } from "../lib/tradingDisplay";
import { EmptyState, ErrorState, LoadingState } from "./states";

export interface ExecutionSignal {
  _id: string;
  agent: string;
  symbol: string;
  direction: string;
  session?: string;
  date: string;
  outcome?: string | null;
  pnl_dollars?: number | null;
  reversal_patterns?: string | null;
  reversal_pattern_score?: number | null;
  signal_type?: string | null;
  trap_score?: number | null;
  absorption_trap?: boolean | null;
  fbd_flow_score?: number | null;
  extension_stddev?: number | null;
  confirmation_score?: number | null;
  volume_exhaustion?: boolean | null;
  exhaustion_score?: number | null;
  rr_tp1?: number | null;
  created_at: number;
}

export interface AgentExecutionSummary {
  agent: string;
  total: number;
  open: number;
  closed: number;
  wins: number;
  losses: number;
  breakevens: number;
  totalPnl: number;
  winRate: number | null;
}

export interface ExecutionClosedQuality {
  pnlSampleCount: number;
  avgPnl: number | null;
  avgWin: number | null;
  avgLoss: number | null;
  profitFactor: number | null;
  payoffRatio: number | null;
}

export interface AgentExecutionQuality {
  agent: string;
  quality: ExecutionClosedQuality;
}

export interface ExecutionIntegritySummary {
  closedMissingPnl: number;
  pendingWithPnl: number;
  unknownAgentRows: number;
}

export interface ExecutionSummary extends AgentExecutionSummary {
  agents: AgentExecutionSummary[];
  closedQuality: ExecutionClosedQuality;
  agentClosedQuality: AgentExecutionQuality[];
  integrity: ExecutionIntegritySummary;
}

const AGENT_ORDER = ["orb", "fbd", "mr"];
const AGENT_LABELS: Record<string, string> = {
  orb: "ORB",
  fbd: "FBD",
  mr: "MR",
};

function emptySummary(agent = "all"): AgentExecutionSummary {
  return {
    agent,
    total: 0,
    open: 0,
    closed: 0,
    wins: 0,
    losses: 0,
    breakevens: 0,
    totalPnl: 0,
    winRate: null,
  };
}

function applySignal(summary: AgentExecutionSummary, signal: ExecutionSignal, lifecycle: SignalLifecycleState) {
  summary.total += 1;
  if (lifecycle === "open") summary.open += 1;
  else summary.closed += 1;
  if (lifecycle === "win") summary.wins += 1;
  if (lifecycle === "loss") summary.losses += 1;
  if (lifecycle === "breakeven") summary.breakevens += 1;
  if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
    summary.totalPnl += signal.pnl_dollars;
  }
}

function finalizeSummary(summary: AgentExecutionSummary): AgentExecutionSummary {
  const decisive = summary.wins + summary.losses;
  return {
    ...summary,
    winRate: decisive > 0 ? summary.wins / decisive : null,
  };
}

function emptyClosedQuality(): ExecutionClosedQuality {
  return {
    pnlSampleCount: 0,
    avgPnl: null,
    avgWin: null,
    avgLoss: null,
    profitFactor: null,
    payoffRatio: null,
  };
}

function emptyIntegritySummary(): ExecutionIntegritySummary {
  return {
    closedMissingPnl: 0,
    pendingWithPnl: 0,
    unknownAgentRows: 0,
  };
}

function hasFinitePnl(signal: ExecutionSignal): boolean {
  return typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars);
}

function normalizedOutcome(signal: ExecutionSignal): string | null {
  return signal.outcome ? signal.outcome.toUpperCase() : null;
}

function isKnownExecutionAgent(agent: string): boolean {
  return AGENT_ORDER.includes(agent);
}

function applyIntegrity(summary: ExecutionIntegritySummary, signal: ExecutionSignal, agent: string) {
  const outcome = normalizedOutcome(signal);
  const hasPnl = hasFinitePnl(signal);
  if (outcome && outcome !== "PENDING" && !hasPnl) summary.closedMissingPnl += 1;
  if (outcome === "PENDING" && hasPnl) summary.pendingWithPnl += 1;
  if (!isKnownExecutionAgent(agent)) summary.unknownAgentRows += 1;
}

function buildClosedQuality(signals: ExecutionSignal[]): ExecutionClosedQuality {
  let pnlSampleCount = 0;
  let totalPnl = 0;
  let winCount = 0;
  let lossCount = 0;
  let grossWin = 0;
  let grossLoss = 0;

  for (const signal of signals) {
    if (typeof signal.pnl_dollars !== "number" || !Number.isFinite(signal.pnl_dollars)) continue;

    pnlSampleCount += 1;
    totalPnl += signal.pnl_dollars;
    if (signal.pnl_dollars > 0) {
      winCount += 1;
      grossWin += signal.pnl_dollars;
    } else if (signal.pnl_dollars < 0) {
      lossCount += 1;
      grossLoss += Math.abs(signal.pnl_dollars);
    }
  }

  if (pnlSampleCount === 0) return emptyClosedQuality();

  const avgWin = winCount > 0 ? grossWin / winCount : null;
  const avgLossMagnitude = lossCount > 0 ? grossLoss / lossCount : null;
  return {
    pnlSampleCount,
    avgPnl: totalPnl / pnlSampleCount,
    avgWin,
    avgLoss: avgLossMagnitude === null ? null : -avgLossMagnitude,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : null,
    payoffRatio: avgWin !== null && avgLossMagnitude !== null && avgLossMagnitude > 0 ? avgWin / avgLossMagnitude : null,
  };
}

export function buildExecutionSummary(signals: ExecutionSignal[]): ExecutionSummary {
  const overall = emptySummary();
  const integrity = emptyIntegritySummary();
  const byAgent = new Map<string, AgentExecutionSummary>();
  const signalsByAgent = new Map<string, ExecutionSignal[]>();

  for (const signal of signals) {
    const agent = (signal.agent || "unknown").toLowerCase();
    const lifecycle = signalLifecycleState(signal);
    applyIntegrity(integrity, signal, agent);
    applySignal(overall, signal, lifecycle);

    const agentSummary = byAgent.get(agent) ?? emptySummary(agent);
    applySignal(agentSummary, signal, lifecycle);
    byAgent.set(agent, agentSummary);

    const agentSignals = signalsByAgent.get(agent) ?? [];
    agentSignals.push(signal);
    signalsByAgent.set(agent, agentSignals);
  }

  const agents = Array.from(byAgent.values())
    .map(finalizeSummary)
    .sort((a, b) => {
      const ai = AGENT_ORDER.indexOf(a.agent);
      const bi = AGENT_ORDER.indexOf(b.agent);
      return (ai === -1 ? AGENT_ORDER.length : ai) - (bi === -1 ? AGENT_ORDER.length : bi)
        || a.agent.localeCompare(b.agent);
    });

  return {
    ...finalizeSummary(overall),
    agents,
    closedQuality: buildClosedQuality(signals),
    agentClosedQuality: agents.map((row) => ({
      agent: row.agent,
      quality: buildClosedQuality(signalsByAgent.get(row.agent) ?? []),
    })),
    integrity,
  };
}

function formatPct(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

function formatNullableDollars(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return formatDollars(value);
}

function formatMultiple(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return value.toFixed(2);
}

function agentLabel(agent: string): string {
  return AGENT_LABELS[agent] ?? agent.toUpperCase();
}

function SummaryMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="exec-acc-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AgentRow({ row }: { row: AgentExecutionSummary }) {
  return (
    <div className="exec-acc-agent-row">
      <strong>{agentLabel(row.agent)}</strong>
      <span>{row.total} signals</span>
      <span>{row.open} open</span>
      <span>{row.wins}W / {row.losses}L / {row.breakevens}BE</span>
      <span>{formatPct(row.winRate)} WR</span>
      <span>{formatDollars(row.totalPnl)}</span>
    </div>
  );
}

function ClosedQuality({ quality }: { quality: ExecutionClosedQuality }) {
  return (
    <div className="exec-acc-quality" aria-label="Closed P&L quality">
      <div className="exec-acc-section-title">Closed P&L quality</div>
      <p>Read-only quality view from signals with realized P&L in this same recent window.</p>
      <div className="exec-acc-quality-grid">
        <div className="exec-acc-quality-card">
          <strong>Avg closed P&L</strong>
          <span>{formatNullableDollars(quality.avgPnl)}</span>
          <small>{quality.pnlSampleCount} P&L samples</small>
        </div>
        <div className="exec-acc-quality-card">
          <strong>Avg win / loss</strong>
          <span>{formatNullableDollars(quality.avgWin)} / {formatNullableDollars(quality.avgLoss)}</span>
        </div>
        <div className="exec-acc-quality-card">
          <strong>Profit factor</strong>
          <span>{formatMultiple(quality.profitFactor)}</span>
        </div>
        <div className="exec-acc-quality-card">
          <strong>Payoff ratio</strong>
          <span>{formatMultiple(quality.payoffRatio)}</span>
        </div>
      </div>
    </div>
  );
}

function ExecutionIntegrity({ integrity }: { integrity: ExecutionIntegritySummary }) {
  const hasGap = integrity.closedMissingPnl > 0 || integrity.pendingWithPnl > 0 || integrity.unknownAgentRows > 0;
  return (
    <div className="exec-acc-quality" aria-label="Execution lifecycle integrity">
      <div className="exec-acc-section-title">Lifecycle integrity</div>
      <p>Read-only close-out hygiene checks from the same recent signal window.</p>
      <div className="exec-acc-quality-grid">
        <div className="exec-acc-quality-card">
          <strong>Closed missing P&L</strong>
          <span>{integrity.closedMissingPnl}</span>
          <small>closed outcomes without <code>pnl_dollars</code></small>
        </div>
        <div className="exec-acc-quality-card">
          <strong>Pending/P&L conflicts</strong>
          <span>{integrity.pendingWithPnl}</span>
          <small>PENDING rows already carrying P&L</small>
        </div>
        <div className="exec-acc-quality-card">
          <strong>Unknown agents</strong>
          <span>{integrity.unknownAgentRows}</span>
          <small>outside ORB/FBD/MR</small>
        </div>
      </div>
      {!hasGap ? <small>No lifecycle integrity gaps detected in this window.</small> : null}
    </div>
  );
}

function AgentClosedQuality({ rows }: { rows: AgentExecutionQuality[] }) {
  return (
    <div className="exec-acc-quality" aria-label="Agent closed P&L quality">
      <div className="exec-acc-section-title">Agent close quality</div>
      <p>Read-only close quality split by agent from the same recent signal window.</p>
      <div className="exec-acc-quality-grid">
        {rows.map(({ agent, quality }) => (
          <div key={agent} className="exec-acc-quality-card">
            <strong>{agentLabel(agent)}</strong>
            <span>{formatNullableDollars(quality.avgPnl)} avg</span>
            <span>{formatNullableDollars(quality.avgWin)} / {formatNullableDollars(quality.avgLoss)}</span>
            <small>PF {formatMultiple(quality.profitFactor)} · payoff {formatMultiple(quality.payoffRatio)} · {quality.pnlSampleCount} samples</small>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ExecutionAccountabilityPanel({ state }: { state: QueryState<ExecutionSignal[]> }) {
  if (state.status === "loading") {
    return <LoadingState label="Execution accountability" block />;
  }

  if (state.status === "error") {
    return <ErrorState error={state.error} onRetry={state.retry} label="Execution accountability" block />;
  }

  const signals = state.data ?? [];
  if (signals.length === 0) {
    return <EmptyState message="No recent execution signals available yet." block />;
  }

  const summary = buildExecutionSummary(signals);

  return (
    <section className="exec-acc-panel" aria-label="Execution accountability">
      <div className="exec-acc-head">
        <div>
          <h3>Execution accountability</h3>
          <p>Recent signal-to-trade lifecycle snapshot. Source: getRecentSignals limit 250.</p>
        </div>
        <span>Read-only</span>
      </div>

      <div className="exec-acc-grid">
        <SummaryMetric label="Recent signals" value={summary.total} />
        <SummaryMetric label="Open" value={summary.open} />
        <SummaryMetric label="Closed" value={summary.closed} />
        <SummaryMetric label="Win rate" value={formatPct(summary.winRate)} />
        <SummaryMetric label="Record" value={`${summary.wins}W / ${summary.losses}L / ${summary.breakevens}BE`} />
        <SummaryMetric label="Realized P&L" value={formatDollars(summary.totalPnl)} />
      </div>

      <ClosedQuality quality={summary.closedQuality} />
      <ExecutionIntegrity integrity={summary.integrity} />
      <AgentClosedQuality rows={summary.agentClosedQuality} />

      <div className="exec-acc-section-title">By agent</div>
      <div className="exec-acc-agent-table">
        {summary.agents.map((row) => (
          <AgentRow key={row.agent} row={row} />
        ))}
      </div>
    </section>
  );
}
